import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../Firebase';
import {
  ShieldCheck, CheckCircle2, ArrowRight, Clock, Download,
  UserCheck, Info, Building, Smartphone, Globe, Briefcase,
  ChevronDown, ChevronUp, Layers, Settings, PhoneCall, Users, X
} from 'lucide-react';

export default function CurrentAccount() {
  const [activeTier, setActiveTier] = useState('regular');
  const [faqOpen, setFaqOpen] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    city: '',
    accountType: 'Current Account'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const entryId = `acc_${Date.now()}`;
    const payload = {
      id: entryId,
      name: formData.name,
      contact: formData.contact,
      email: formData.email,
      city: formData.city,
      accountType: formData.accountType,
      status: 'Open',
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(db, 'account_enquiries', entryId), payload);
    } catch (error) {
      console.warn("Could not save account enquiry online:", error);
    }
    alert(`Thank you, ${formData.name}! Your application for a ${formData.accountType} has been received. Our executive will contact you at ${formData.contact} within 24 hours.`);
    setIsModalOpen(false);
    setFormData({
      name: '',
      contact: '',
      email: '',
      city: '',
      accountType: 'Current Account'
    });
  };

  const accountTiers = {
    regular: {
      name: 'Regular Current Account',
      tagline: 'Standard ledger for small business merchants',
      minBalance: '₹5,000 (Average Monthly Balance)',
      target: 'Retail Shop Owners, Sole Proprietors, Traders',
      features: [
        'Unlimited deposits and withdrawals at parent branch.',
        'Free Monthly E-Statements to track business cash flows.',
        'Complimentary 50-leaf multicity cheque books per quarter.',
        'Access to basic mobile and internet business portals.'
      ],
      icon: Briefcase
    },
    premium: {
      name: 'Business Premium Account',
      tagline: 'High-volume account with premium cash limits',
      minBalance: '₹25,000 (Average Monthly Balance)',
      target: 'Medium enterprises, manufacturers, contractors',
      features: [
        'High daily free cash deposit limits up to ₹10 Lakhs.',
        'Dedicated corporate relationship manager support.',
        'Zero-fee bulk RTGS/NEFT payment transfers.',
        'Free first-year premium business platinum debit cards.'
      ],
      icon: Layers
    },
    merchant: {
      name: 'Merchant Special Account',
      tagline: 'Integrated digital account for retailers',
      minBalance: '₹10,000 (Or linked POS active integration)',
      target: 'Supermarkets, clinics, showrooms, e-commerce vendors',
      features: [
        'Complementary QR Code and POS Swipe terminals deployment.',
        'Zero rental fees for POS machine if monthly volume exceeds criteria.',
        'Same-day credit settlement for all card/UPI transactions.',
        'SMS/Email transaction confirmations sent in real-time.'
      ],
      icon: Smartphone
    },
    institutional: {
      name: 'Cooperative & Trust Ledger',
      tagline: 'Special accounts for social & local institutions',
      minBalance: '₹2,000 (Exempted for specific government tasks)',
      target: 'NGOs, Housing Societies, Clubs, Trusts, Schools',
      features: [
        'Exempted from standard transaction taxes where applicable.',
        'Multiple authorized signatory access with layered approvals.',
        'Auto-debit setup for utility payments & society maintenance.',
        'Audit-ready ledger exports with detailed transaction commentary.'
      ],
      icon: Building
    }
  };

  const currentCharges = [
    { service: 'Account Opening Charges', charge: 'Nil' },
    { service: 'Minimum Balance Non-Maintenance', charge: '₹250 per month' },
    { service: 'Cash Deposit Limit (Free)', charge: 'Up to ₹5 Lakhs per month (₹2 per ₹1,000 beyond)' },
    { service: 'Cash Withdrawal Limit', charge: 'Unlimited free at parent branch' },
    { service: 'RTGS / NEFT Fees', charge: 'Free (Online) | Nominal branch charges' },
    { service: 'Cheque Book (First 50 leaves)', charge: 'Free (₹3 per additional leaf)' },
    { service: 'POS Terminal Rent', charge: '₹300/month (Waived on high card transaction volumes)' }
  ];

  const businessDocs = {
    proprietorship: [
      'PAN Card of the Proprietor',
      'Aadhaar Card of the Proprietor',
      'Shop & Establishment License (Gumasta)',
      'GST Registration Certificate (if applicable)',
      'Recent Utility Bill of the business location'
    ],
    partnership: [
      'Registered Partnership Deed',
      'Partnership Firm registration certificate',
      'PAN Card of the Partnership Firm',
      'Aadhaar & PAN Cards of all partners',
      'Authorization letter signed by all partners'
    ],
    corporate: [
      'Certificate of Incorporation & Memorandum of Association (MoA)',
      'Board Resolution authorizing opening & operating the account',
      'PAN Card of the Company',
      'Aadhaar & PAN Cards of all authorized directors',
      'Utility bill in the name of the Company'
    ]
  };

  const faqs = [
    {
      q: 'Who can open a Current Account with Sahyadri?',
      a: 'Sole Proprietorship firms, Partnership firms, Private & Public Limited Companies, Registered Societies, local Clubs, Educational institutions, and Public Trusts can open a Current Account.'
    },
    {
      q: 'What is the interest rate paid on Current Accounts?',
      a: 'As per Reserve Bank of India (RBI) directives, no interest is paid on deposits held in Current Accounts. These are strictly liquidity-centric transaction accounts.'
    },
    {
      q: 'Is there a limit on daily cash withdrawals?',
      a: 'There is no limit on withdrawals at your home branch. For non-home branches, a standard daily limit is applied for security reasons. Cash transaction taxes apply beyond RBI prescribed thresholds.'
    },
    {
      q: 'Can we avail overdraft (OD) facilities on Current Accounts?',
      a: 'Yes! Sahyadri offers secured and unsecured Overdraft facilities to business account holders based on credit evaluations, ledger turnovers, and collateral deposits.'
    },
    {
      q: 'Do you provide digital payment tools like POS swipe machines?',
      a: 'Yes, we provide modern Android-based POS terminal machines and soundboxes for business checkout desks. Speak with our branch representative to install them.'
    }
  ];

  const toggleFaq = (index) => {
    setFaqOpen(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="pt-[68px] bg-neutralBg font-sans text-gray-700 min-h-screen">
      {/* 1. HERO HEADER */}
      <section
        className="relative overflow-hidden text-white py-24 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "linear-gradient(rgba(5, 32, 70, 0.9), rgba(5, 32, 70, 0.95)), url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(179,24,37,0.15),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-6 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-white/10 px-4 py-1.5 rounded-full inline-block backdrop-blur-sm border border-white/10">
            Commercial Business Portfolios
          </span>
          <h1 className="font-montserrat font-extrabold text-4xl sm:text-5xl md:text-6xl text-white tracking-tight leading-tight">
            Business Current Accounts
          </h1>
          <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto font-light leading-relaxed">
            Power your business with high-volume liquidity, instant digital transactions, and zero-fee transfer systems. Build a robust financial pathway for your enterprise.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-3.5 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-lg transform hover:-translate-y-0.5"
            >
              Apply for Account
            </button>
            <a href="#charges" className="px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all border border-white/20 backdrop-blur-sm">
              View Service Charges
            </a>
          </div>
        </div>
      </section>

      {/* 2. KEY HIGHLIGHTS CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex items-start space-x-4">
            <div className="p-3 bg-[#B31825]/10 text-[#B31825] rounded-xl">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-xs text-gray-400 font-bold uppercase tracking-wider">Interest Rate</h4>
              <p className="text-lg font-bold text-[#052046] mt-1">0.00% p.a.</p>
              <p className="text-xs text-gray-500 mt-0.5 font-light">As per regulatory RBI guidelines</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex items-start space-x-4">
            <div className="p-3 bg-[#052046]/10 text-[#052046] rounded-xl">
              <Briefcase className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-xs text-gray-400 font-bold uppercase tracking-wider">Minimum Balance</h4>
              <p className="text-lg font-bold text-[#052046] mt-1">Starts at ₹5,000</p>
              <p className="text-xs text-gray-500 mt-0.5 font-light">Competitive commercial thresholds</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex items-start space-x-4">
            <div className="p-3 bg-[#00A86B]/10 text-[#00A86B] rounded-xl">
              <Globe className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-xs text-gray-400 font-bold uppercase tracking-wider">Digital Transfers</h4>
              <p className="text-lg font-bold text-[#052046] mt-1">Unlimited Free</p>
              <p className="text-xs text-gray-500 mt-0.5 font-light">Free online RTGS/NEFT transactions</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex items-start space-x-4">
            <div className="p-3 bg-purple-500/10 text-purple-600 rounded-xl">
              <Settings className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-xs text-gray-400 font-bold uppercase tracking-wider">Business Solutions</h4>
              <p className="text-lg font-bold text-[#052046] mt-1">POS & QR Codes</p>
              <p className="text-xs text-gray-500 mt-0.5 font-light">Same day settlement channels</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. DYNAMIC TIERS SECTION */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full">
            Commercial Tiers
          </span>
          <h2 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-[#052046]">
            Current Accounts Designed to Scale
          </h2>
          <p className="text-gray-500 text-sm sm:text-base font-light">
            Whether you operate a local retail store or manage a complex manufacturing firm, we have structured accounts matching your volume.
          </p>
        </div>

        {/* Tier Tabs Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Tabs panel */}
          <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-1 gap-3">
            {Object.keys(accountTiers).map((key) => {
              const TierIcon = accountTiers[key].icon;
              const isActive = activeTier === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTier(key)}
                  className={`flex items-center space-x-3 p-4 rounded-xl text-left border transition-all ${isActive
                    ? 'bg-[#052046] text-white border-[#052046] shadow-md transform translate-x-1'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  <TierIcon className={`h-5 w-5 shrink-0 ${isActive ? 'text-white' : 'text-[#B31825]'}`} />
                  <span className="text-xs sm:text-sm font-semibold truncate">{accountTiers[key].name}</span>
                </button>
              );
            })}
          </div>

          {/* Details Card */}
          <div className="lg:col-span-8 bg-white border border-gray-200 rounded-3xl p-8 shadow-md relative overflow-hidden min-h-[380px] flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#B31825]/5 rounded-bl-full pointer-events-none" />
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="font-montserrat font-extrabold text-2xl text-[#052046]">
                  {accountTiers[activeTier].name}
                </h3>
                <p className="text-sm text-[#B31825] font-medium">{accountTiers[activeTier].tagline}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100 text-sm">
                <div>
                  <span className="text-xs text-gray-400 font-bold block uppercase">Minimum Balance Requirement</span>
                  <span className="font-semibold text-gray-800">{accountTiers[activeTier].minBalance}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-bold block uppercase">Target Segment</span>
                  <span className="font-semibold text-gray-800">{accountTiers[activeTier].target}</span>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-xs uppercase tracking-wider text-gray-400">Services & Account Privileges</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs sm:text-sm">
                  {accountTiers[activeTier].features.map((feat, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-[#00A86B] shrink-0 mt-0.5" />
                      <span className="text-gray-600 font-light leading-snug">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4 mt-6">
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <ShieldCheck className="h-4 w-4 text-[#B31825]" />
                <span>KYC & Business registrations mandatory</span>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center space-x-2 px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow"
              >
                <span>Select & Enquire</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. BUSINESS REQUIRED DOCUMENTS SECTION */}
      <section className="bg-white py-20 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">KYC Documents by Business Entity</h3>
            <p className="text-gray-500 text-sm font-light mt-2">Select your commercial registration category to verify mandatory documents needed.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Proprietorship */}
            <div className="bg-[#F7F9FC] border border-gray-200 p-6 rounded-3xl space-y-5">
              <div className="flex items-center space-x-3 text-[#B31825]">
                <UserCheck className="h-5 w-5" />
                <h4 className="font-montserrat font-extrabold text-base text-[#052046]">Proprietorship Firms</h4>
              </div>
              <ul className="space-y-3 text-xs sm:text-sm text-gray-600 font-light">
                {businessDocs.proprietorship.map((doc, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-[#B31825] mr-2 font-bold">•</span>
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Partnership */}
            <div className="bg-[#F7F9FC] border border-gray-200 p-6 rounded-3xl space-y-5">
              <div className="flex items-center space-x-3 text-[#B31825]">
                <Users className="h-5 w-5" />
                <h4 className="font-montserrat font-extrabold text-base text-[#052046]">Partnership Firms</h4>
              </div>
              <ul className="space-y-3 text-xs sm:text-sm text-gray-600 font-light">
                {businessDocs.partnership.map((doc, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-[#B31825] mr-2 font-bold">•</span>
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Corporate */}
            <div className="bg-[#F7F9FC] border border-gray-200 p-6 rounded-3xl space-y-5">
              <div className="flex items-center space-x-3 text-[#B31825]">
                <Building className="h-5 w-5" />
                <h4 className="font-montserrat font-extrabold text-base text-[#052046]">Private/Public Cos. & Trusts</h4>
              </div>
              <ul className="space-y-3 text-xs sm:text-sm text-gray-600 font-light">
                {businessDocs.corporate.map((doc, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-[#B31825] mr-2 font-bold">•</span>
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CHARGES & FEES TABLE */}
      <section id="charges" className="py-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">Current Service Charges & Limits</h3>
          <p className="text-gray-500 text-sm font-light">Transparent service fees designed for commercial growth.</p>
        </div>

        <div className="overflow-hidden border border-gray-200 rounded-3xl shadow-md bg-white">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="p-5 text-xs font-bold uppercase tracking-wider text-[#052046]">Banking Service</th>
                <th className="p-5 text-xs font-bold uppercase tracking-wider text-[#052046]">Limits / Fees Applicable</th>
              </tr>
            </thead>
            <tbody className="text-xs sm:text-sm divide-y divide-gray-100">
              {currentCharges.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50/50">
                  <td className="p-5 text-gray-800 font-semibold">{row.service}</td>
                  <td className="p-5 text-gray-600 font-light">{row.charge}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 6. BUSINESS ONBOARDING ROADMAP */}
      <section className="bg-white py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">Onboarding Your Business</h3>
            <p className="text-gray-500 text-sm font-light mt-2">Get your corporate account set up in 4 simple stages.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Select Profile & Form', desc: 'Choose your tier (Regular/Premium) and fill out the physical corporate form.' },
              { step: '02', title: 'Prepare Business Proofs', desc: 'Gather Shop Acts, GST copies, partnership deeds, or corporate incorporation certificates.' },
              { step: '03', title: 'Branch Visit / Biometrics', desc: 'Authorized partners or directors complete verify biometrics and submit documents.' },
              { step: '04', title: 'Instant Activation', desc: 'Once documents are verified, your business account is activated within 24-48 working hours.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-neutralBg border border-gray-200 p-6 rounded-2xl relative hover:border-[#B31825] transition-all group">
                <span className="text-4xl font-extrabold text-gray-300 group-hover:text-[#B31825]/25 font-mono transition-colors block">
                  {item.step}
                </span>
                <h5 className="font-montserrat font-extrabold text-sm text-[#052046] mt-4 uppercase tracking-wider">
                  {item.title}
                </h5>
                <p className="text-gray-500 text-xs font-light mt-2 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FAQS ACCORDION */}
      <section className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-2">
          <Info className="h-8 w-8 text-[#B31825] mx-auto" />
          <h3 className="font-montserrat font-extrabold text-3xl text-[#052046]">Current Account FAQs</h3>
          <p className="text-gray-500 text-sm font-light">Common commercial questions answered</p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = !!faqOpen[idx];
            return (
              <div key={idx} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex justify-between items-center p-5 text-left focus:outline-none"
                >
                  <span className="font-montserrat font-extrabold text-sm sm:text-base text-[#052046]">
                    {faq.q}
                  </span>
                  {isOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#B31825] shrink-0 ml-4" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 shrink-0 ml-4" />
                  )}
                </button>

                <div className={`transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="p-5 pt-0 border-t border-gray-100 text-xs sm:text-sm text-gray-500 leading-relaxed font-light">
                    {faq.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 8. DOWNLOADS CENTER */}
      <section className="bg-[#052046] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-montserrat font-extrabold text-3xl text-white">Business Downloads</h3>
            <p className="text-gray-300 text-sm font-light mt-2">Get quick access to business ledger forms and applications.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Current Account Opening Form', size: '1.5 MB', link: '#', desc: 'Onboarding form for Sole Proprietors, Partnerships, and Corporates.' },
              { title: 'POS Swipe Machine Agreement', size: '920 KB', link: '#', desc: 'Terms and application to lease merchant payment swipe terminals.' },
              { title: 'IFSC & Branch Code Booklet', size: '2.1 MB', link: '#', desc: 'Central routing and clearing codes across all Sahyadri networks.' }
            ].map((doc, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col justify-between hover:bg-white/10 transition-all">
                <div className="space-y-3">
                  <div className="p-2.5 bg-white/10 rounded-xl w-fit text-[#B31825]">
                    <Download className="h-5 w-5" />
                  </div>
                  <h4 className="font-montserrat font-bold text-sm text-white">{doc.title}</h4>
                  <p className="text-gray-300 text-xs font-light leading-relaxed">{doc.desc}</p>
                </div>
                <div className="pt-6 flex justify-between items-center text-xs">
                  <span className="text-gray-400 font-mono">{doc.size}</span>
                  <a href={doc.link} className="text-[#B31825] hover:underline font-bold tracking-wider uppercase flex items-center">
                    <span>PDF Form</span>
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. CALL TO ACTION */}
      <section className="bg-white border-t border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#B31825] to-[#8a0f19] text-white rounded-3xl p-8 sm:p-12 shadow-xl flex flex-col lg:flex-row justify-between items-center gap-8">
            <div className="space-y-3 text-center lg:text-left">
              <h3 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-white">Equip Your Business Today</h3>
              <p className="text-white/80 text-sm max-w-xl font-light">
                Need customized overdraft structures? Contact our business desks to schedule an executive callback.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 justify-center shrink-0">
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-8 py-3.5 bg-white text-[#B31825] hover:bg-gray-100 font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md"
              >
                Apply for Account
              </button>
              <a href="tel:18001234567" className="px-8 py-3.5 bg-[#052046] hover:bg-[#0c346a] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md flex items-center space-x-2">
                <PhoneCall className="h-4 w-4" />
                <span>Call Helpline</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 10. APPLICATION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100 relative animate-fadeIn">

            {/* Modal Header */}
            <div className="bg-[#052046] text-white p-6 relative">
              <h3 className="font-montserrat font-extrabold text-lg text-white">Apply for Sahyadri Account</h3>
              <p className="text-xs text-gray-300 mt-1 font-light">Fill out your contact details to start onboarding.</p>
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 p-1 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Full Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Contact Number</label>
                <input
                  type="tel"
                  name="contact"
                  required
                  placeholder="Enter 10-digit mobile"
                  pattern="[0-9]{10}"
                  value={formData.contact}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">City / Location</label>
                <input
                  type="text"
                  name="city"
                  required
                  placeholder="Enter your city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-[#052046] uppercase">Account Type</label>
                <select
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleInputChange}
                  className="w-full bg-neutralBg border border-gray-200 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
                >
                  <option value="Savings Account">Savings Account</option>
                  <option value="Current Account">Current Account</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
